import React from 'react';
import { Row, Col, FormGroup, Label } from 'reactstrap';
import { Field, Input } from '@availity/form';
import InputMask from 'react-input-mask';
import { PracticeLocationData } from '../UserPages/Login/PracticeRegistration/Model/PracticeLocationData';
import Helpers from '../../utils/helper';
import {
    FteDvmOption,
    StateOption,
} from '../UserPages/Login/Modals/PracticeRegistrationForm/types';
import InfoTooltip from '../../components/info-tooltip/info-tooltip';
import {
    LocationTypeOptions,
    LocationTypes,
    locationTypeTooltipHtml,
} from '../../slices/marketplace/timeline/models/enums/location-type.enum';
import { useServiceFocuses } from '../../custom-hooks/useServiceFocuses';
import { isOtherFocus } from '../UserPages/Login/Modals/PracticeRegistrationForm/utils';

interface PracticeLocationItemProps {
    practiceLocationData: PracticeLocationData;
    index: number;
    isEditMode?: boolean;
    allStates?: StateOption[];
    fteDvmsRange?: FteDvmOption[];
}

const PracticeLocationItem = (props: PracticeLocationItemProps) => {
    const serviceFocuses = useServiceFocuses();
    const renderLocationField = (
        label: string,
        value: string | number | undefined
    ) => (
        <FormGroup>
            <Label>{label}</Label>
            <h6>{value || 'N/A'}</h6>
        </FormGroup>
    );

    const practiceFocusLabel = isOtherFocus(
        serviceFocuses,
        props.practiceLocationData.practiceFocus
    )
        ? props.practiceLocationData.otherPracticeType ||
          'Other (not specified)'
        : serviceFocuses.find(
              (item) => item.id === props.practiceLocationData.practiceFocus
          )?.name || 'N/A';

    if (props.isEditMode) {
        return (
            <div key={`location-${props.index}`} className="location-section">
                <h5 style={{ marginBottom: '1rem', color: '#495057' }}>
                    Location {props.index + 1}
                </h5>
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Practice Name</Label>
                            <Field
                                type="text"
                                name={`practiceLocationData[${props.index}].practiceLocationName`}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Street Address</Label>
                            <Field
                                type="text"
                                name={`practiceLocationData[${props.index}].address`}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <FormGroup>
                            <Label>City</Label>
                            <Field
                                type="text"
                                name={`practiceLocationData[${props.index}].city`}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label>State</Label>
                            <Input
                                type="select"
                                name={`practiceLocationData[${props.index}].state`}>
                                {[
                                    { id: -1, name: '' },
                                    ...(props.allStates || []),
                                ].map((item) => (
                                    <option
                                        value={item.name}
                                        key={`state${item.id}`}>
                                        {item.name}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label>ZIP Code</Label>
                            <Field
                                type="text"
                                name={`practiceLocationData[${props.index}].zipCode`}
                                maxLength={5}
                                onKeyDown={(
                                    event: React.KeyboardEvent<HTMLInputElement>
                                ) => {
                                    const key = event.keyCode || event.charCode;
                                    const digits = key > 47 && key < 58;
                                    const validControls =
                                        key == 8 || key == 9 || key == 46;
                                    if (!(digits || validControls)) {
                                        event.preventDefault();
                                    }
                                }}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Phone</Label>
                            <Field
                                type="text"
                                name={`practiceLocationData[${props.index}].phone`}
                                component={InputMask}
                                mask="(999) 999-9999"
                                placeholder="(999) 999-9999"
                                maskChar={null}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Website</Label>
                            <Field
                                type="text"
                                name={`practiceLocationData[${props.index}].website`}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Service Focus</Label>
                            <Input
                                type="select"
                                name={`practiceLocationData[${props.index}].practiceFocus`}>
                                {[
                                    { id: '', name: 'Select' },
                                    ...serviceFocuses,
                                ].map((item) => (
                                    <option
                                        value={item.id}
                                        key={`practiceFocus${item.id}`}>
                                        {item.name}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Number of FTE DVMs</Label>
                            <Input
                                type="select"
                                name={`practiceLocationData[${props.index}].fteDvm`}>
                                {[
                                    { id: '', name: 'Select' },
                                    ...(props.fteDvmsRange || []),
                                ].map((item) => (
                                    <option
                                        value={item.id}
                                        key={`fteDvm${item.id}`}>
                                        {item.name}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label>
                                Location Type
                                <InfoTooltip
                                    idText="location-type-item-tooltip"
                                    text=""
                                    html={locationTypeTooltipHtml}
                                    place="top"
                                />
                            </Label>
                            <Input
                                type="select"
                                name={`practiceLocationData[${props.index}].locationType`}>
                                {[
                                    { id: '', name: 'Select' },
                                    ...LocationTypes,
                                ].map((item) => (
                                    <option
                                        value={item.id}
                                        key={`locationType${item.id}`}>
                                        {item.name}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    </Col>
                </Row>
            </div>
        );
    }

    // View mode
    return (
        <div key={`location-${props.index}`} className="location-section">
            <h5 style={{ marginBottom: '1rem', color: '#495057' }}>
                Location {props.index + 1}
            </h5>
            <Row>
                <Col md={6}>
                    {renderLocationField(
                        'Practice Name',
                        props.practiceLocationData.practiceLocationName
                    )}
                </Col>
                <Col md={6}>
                    {renderLocationField(
                        'Street Address',
                        props.practiceLocationData.address
                    )}
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    {renderLocationField(
                        'City',
                        props.practiceLocationData.city
                    )}
                </Col>
                <Col md={4}>
                    {renderLocationField(
                        'State',
                        props.practiceLocationData.state
                    )}
                </Col>
                <Col md={4}>
                    {renderLocationField(
                        'ZIP Code',
                        props.practiceLocationData.zipCode
                    )}
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    {renderLocationField(
                        'Phone',
                        Helpers.getFormattedPhoneNumberWithoutPrefix(
                            props.practiceLocationData.phone
                        )
                    )}
                </Col>
                <Col md={6}>
                    {renderLocationField(
                        'Website',
                        props.practiceLocationData.website
                    )}
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    {renderLocationField('Service Focus', practiceFocusLabel)}
                </Col>
                <Col md={6}>
                    {renderLocationField(
                        'Number of FTE DVMs',
                        props.practiceLocationData.fteDvm
                    )}
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label>
                            Location Type
                            <InfoTooltip
                                idText="location-type-view-tooltip"
                                text=""
                                html={locationTypeTooltipHtml}
                                place="top"
                            />
                        </Label>
                        <h6>
                            {props.practiceLocationData.locationType
                                ? LocationTypeOptions[
                                      props.practiceLocationData.locationType
                                  ] || 'N/A'
                                : 'N/A'}
                        </h6>
                    </FormGroup>
                </Col>
            </Row>
        </div>
    );
};

export default PracticeLocationItem;
